import {
  Component,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  input,
  linkedSignal,
} from '@angular/core';

export type StoreLogoLoading = 'lazy' | 'eager';

/**
 * What the wrapper clips to. The component rounds its own image and fallback so a circular wrapper
 * needs nothing else; a square wrapper passes `square` so no corner is rounded off inside it.
 */
export type StoreLogoShape = 'circle' | 'square';

/**
 * How many characters of the longest word fit across the circle at full size. See `fit`.
 *
 * Measured, not guessed: DM Sans Bold averages 0.56em per character, so at the stylesheet's 22cqi
 * about 6.5 characters span the 80% of the circle left inside the padding — and words heavy in
 * wide glyphs ("KeepCup", "Company") need less than that. Over 400 real logo-less store names, 8
 * (the store hero's number) broke 69% of them mid-word at tile sizes; 5.5 brings that to 8% at
 * 72px and 3% at 92px without pushing many more names down to the 7px floor.
 */
const FIT_CHARS = 5.5;

/**
 * A store's logo, or the store's name standing in for one.
 *
 * Every surface that shows a store logo — the store hero, the trending tiles, the top-stores row,
 * the compare cards — needs the same two things: paint the logo when there is one, and paint
 * something that still names the brand when there is not. This is that pair, lifted from the store
 * page's hero so the rest of the site cannot drift from it. The fallback is the whole store name on
 * a white ground, not initials: two letters in a tinted circle say nothing about which brand a
 * reader is looking at, and read as a placeholder rather than as a wordmark.
 *
 * It is purely presentational and sizes to whatever wraps it. The wrapper owns the slot — its
 * width, height, border-radius and any click-out — and this fills it, which is what keeps every
 * surface's spacing exactly where it was before the swap.
 *
 * `ViewEncapsulation.None` is deliberate, as with `app-coupon-card`: the rules are namespaced under
 * `.sav-store-logo-*`, which nothing else on the site uses, and being global lets a surface reach
 * `.sav-store-logo-image` from its own stylesheet where it used to reach its `<img>`.
 *
 * ```html
 * <span class="sav-store-tile-logo-box">
 *   <app-store-logo [logoUrl]="store.logoUrl" [name]="store.name" decorative />
 * </span>
 * ```
 */
@Component({
  selector: 'app-store-logo',
  imports: [],
  templateUrl: './store-logo.html',
  styleUrl: './store-logo.scss',
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'sav-store-logo',
    '[class.sav-store-logo-square]': 'shape() === "square"',
  },
})
export class StoreLogo {
  /** Already resolved through `AssetUrlPipe` or equivalent — '' when the store has no logo. */
  readonly logoUrl = input('');
  readonly name = input.required<string>();
  /** The image's alt text; the store name when left empty. Ignored when `decorative`. */
  readonly alt = input('');
  /** `lazy` unless the slot is in the first viewport — the store hero is the one `eager` case. */
  readonly loading = input<StoreLogoLoading>('lazy');
  /**
   * For slots whose caption or wrapper already names the store. The fallback is hidden from
   * assistive tech, so the name is not announced twice. The image gets "<name> logo" rather than
   * an empty alt: Bing Webmaster's SEO report flags every empty alt as "Alt attribute for images
   * is missing" (38 on one store page, owner asked for the fix on 2026-11-05).
   */
  readonly decorative = input(false, { transform: booleanAttribute });
  readonly shape = input<StoreLogoShape>('circle');

  /**
   * True once the logo image has failed to load, so the name takes over.
   *
   * A missing `logoUrl` is handled by the template, but a URL pointing at a file that 404s or
   * decodes to nothing was not: the <img> rendered, drew nothing, and left an empty circle where a
   * brand should be. That is indistinguishable from a broken page to a reader.
   *
   * Linked to `logoUrl` rather than a plain signal so it resets when the URL changes — a page that
   * keeps its component across store navigations must not carry one store's failure onto the next.
   */
  protected readonly failed = linkedSignal({ source: this.logoUrl, computation: () => false });

  protected readonly showImage = computed(() => Boolean(this.logoUrl()) && !this.failed());

  protected readonly imageAlt = computed(() =>
    this.decorative() ? `${this.name()} logo` : this.alt() || this.name(),
  );

  /**
   * How much to scale the fallback name so it fits inside the circle.
   *
   * The binding constraint is the longest single word — the text wraps between words, but a word
   * itself cannot break, so "Kingdom" is what decides whether the name overflows, not the total
   * length. A word up to `FIT_CHARS` long fits at full size; anything longer scales down in
   * proportion. CSS cannot measure text, which is why this is computed here and handed to the
   * stylesheet as a custom property.
   *
   * Same shape as the store hero's `storeLogoTextFit()`, with the constant calibrated to the face
   * (see `FIT_CHARS`) and the whitespace split it meant to do — its `/s+/` splits on the letter s.
   */
  protected readonly fit = computed(() => {
    const words = this.name().trim().split(/\s+/).filter(Boolean);
    const longest = words.reduce((max, word) => Math.max(max, word.length), 0);

    return Math.min(1, FIT_CHARS / Math.max(longest, FIT_CHARS)).toFixed(3);
  });

  protected onError(): void {
    this.failed.set(true);
  }
}
